/**
 * Bulk Edit Dialog for Timesheet entries
 *
 * Allows editing multiple selected timesheet entries at once.
 * Supports changing: matter, user (admin only), rate, billable status.
 */

import { memo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MatterCombobox, UserCombobox } from '@/components/common/EntityCombobox';

export interface BulkEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onApply: (updates: Record<string, unknown>) => void;
  matters: Array<{ id: number; name: string; group_name?: string }>;
  users: Array<{ id: number; first_name: string; last_name: string }>;
  canManageUsers: boolean;
}

export const BulkEditDialog = memo(function BulkEditDialog({
  open,
  onOpenChange,
  selectedCount,
  onApply,
  matters,
  users,
  canManageUsers,
}: BulkEditDialogProps) {
  const [updates, setUpdates] = useState<Record<string, unknown>>({});

  const handleSubmit = () => {
    if (Object.keys(updates).length === 0) return;
    onApply(updates);
    setUpdates({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Bulk Edit {selectedCount} Entries</DialogTitle>
          <DialogDescription>
            Select which fields to update across all selected entries.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Matter</Label>
              <MatterCombobox
                value={(updates.matter as number) ?? null}
                onChange={(id) => id && setUpdates({ ...updates, matter: id })}
                matters={matters}
                includeInactive
                placeholder="Change matter..."
              />
            </div>

            {canManageUsers && (
              <div className="space-y-2">
                <Label>User</Label>
                <UserCombobox
                  value={(updates.user as number) ?? null}
                  onChange={(id) => id && setUpdates({ ...updates, user: id })}
                  users={users}
                  placeholder="Change user..."
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rate</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="New rate..."
                className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                style={{ MozAppearance: 'textfield' }}
                onChange={(e) =>
                  setUpdates({ ...updates, rate: e.target.value ? parseFloat(e.target.value) : null })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Billable Status</Label>
              <Select onValueChange={(value) => setUpdates({ ...updates, billable: value === 'true' })}>
                <SelectTrigger>
                  <SelectValue placeholder="Change billable..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Billable</SelectItem>
                  <SelectItem value="false">Non-billable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={Object.keys(updates).length === 0}>
            Apply Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
